import React, { useState } from 'react';
import { InformationCircleIcon } from '@heroicons/react/outline';
import ShareReferralModal from './ShareReferralModal';

const generatePebbleCode = (userId) => {
  const digits = '0123456789';
  
  if (!userId) {
    let code = '';
    for (let i = 0; i < 5; i++) {
      code += digits[Math.floor(Math.random() * digits.length)];
    }
    return `PEB${code}`;
  }
  
  let code = '';
  const hash = userId.slice(-8);
  for (let i = 0; i < 5; i++) {
    const charCode = hash.charCodeAt(i % hash.length) + i;
    code += digits[charCode % digits.length];
  }
  
  return `PEB${code}`;
};

const ReferralCard = ({ userDetails }) => {
  const [showInfo, setShowInfo] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  
  // Always use PEB format for referral codes
  // Only use backend code if it's already in PEB format, otherwise generate new one
  const backendCode = userDetails?.referralCode;
  const isPebFormat = backendCode && backendCode.startsWith('PEB');
  const referralCode = isPebFormat ? backendCode : generatePebbleCode(userDetails?.uid);
  
  const referralUrl = `${window.location.origin}/signup?ref=${referralCode}`;

  return (
    <div className="bg-base-white w-full rounded-lg shadow-sm border border-grey-200 p-3">
      <div className="flex justify-between items-center mb-1">
        <h2 className="text-sm font-semibold text-grey-700">Organizer Referral Code</h2>
        <div className="relative">
          <button
            onMouseEnter={() => setShowInfo(true)}
            onMouseLeave={() => setShowInfo(false)}
            onClick={() => setShowInfo(!showInfo)}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <InformationCircleIcon className="h-5 w-5" />
          </button>
          {showInfo && (
            <div className="absolute right-0 top-7 z-50 w-72 bg-white rounded-lg shadow-lg border border-gray-200 p-4 text-left">
              <div className="absolute -top-2 right-2 w-3 h-3 bg-white border-l border-t border-gray-200 transform rotate-45"></div>
              <h3 className="font-semibold text-gray-900 text-sm mb-1">Earn free months of Pro</h3>
              <p className="text-xs text-gray-600 mb-3">
                Every 5 organizers who sign up with your code = <strong>1 free month</strong> of your current plan.
              </p>
              <ul className="text-xs text-gray-600 space-y-1.5">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary-500 flex-shrink-0" />
                  Basic → earn a free month of Pro
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary-500 flex-shrink-0" />
                  Pro → earn free months of Pro
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary-500 flex-shrink-0" />
                  Premium → earn free months of Premium
                </li>
              </ul>
              <div className="mt-3 pt-3 border-t border-gray-100">
                <p className="text-xs text-gray-500">
                  This code is for event organizers only — not vendors.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
      <p className="text-xs text-grey-500 mb-2">5 referrals = 1 free month of Pro</p>
      <div className="flex flex-col gap-2">
        <div className="text-lg font-mono font-semibold text-grey-900 tracking-wide">
          {referralCode}
        </div>
        <button
          onClick={() => setShareModalOpen(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white text-xs font-semibold rounded-lg transition-colors shadow-sm w-fit"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
          </svg>
          Share with Organizers
        </button>
        <ShareReferralModal
          isOpen={shareModalOpen}
          onClose={() => setShareModalOpen(false)}
          referralCode={referralCode}
          referralUrl={referralUrl}
        />
      </div>
    </div>
  );
};

export default ReferralCard; 